import { RefreshCw } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatDate, formatDateTime } from "../api";
import { useApi } from "../hooks";
import { useAuth } from "../auth";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  StatusBadge,
} from "../components/Ui";
import Dialog from "../components/Dialog";
import FilterPill from "../components/FilterPill";
import { label, sentence, Value } from "../components/Workflow";
type Approval = {
  id: number;
  client_name: string;
  action_type: string;
  risk_level: string;
  status: string;
  description: string;
  reasoning: string;
  ai_reasoning: string;
  confidence_level: string;
  expected_outcome: string;
  risk_if_wrong: string;
  measurement_criteria: string;
  notes: string;
  payload: unknown;
  submitted_at: string;
  submitted_by_name: string;
  reviewed_by_name: string;
  /** Google Ads changes: the latest apply, from ads_changes. */
  ads_status?: "applying" | "applied" | "failed" | "undone" | "undo_failed" | null;
  ads_error?: string | null;
  ads_undoable?: boolean | null;
};
/** A Google Ads change NOLA applies when it is approved (it carries the exact operations). */
const ADS_ACTIONS = new Set(["ads_campaign_create", "ads_campaign_status", "ads_budget_change", "ads_negative_add", "ads_keyword_add", "ads_placement_exclude", "ads_automation_off"]);
const isAdsChange = (a: Approval) => { const p = proposed(a.payload); return ADS_ACTIONS.has(a.action_type) && Array.isArray(p.operations) && p.operations.length > 0 && !!p.google_ads_account && typeof p.seal === "string"; };
// Stored keys ("pause_campaign", "MEDIUM") read as words wherever they show; a Google Ads change has its own title.
const actionLabel = (a: Approval) => { const t = proposed(a.payload).title; return typeof t === "string" && t ? t : sentence(a.action_type || "action"); };
/** The status a reviewer reads: a Google Ads change says whether it reached Google. */
function statusOf(a: Approval): { text: string; tone: "success" | "danger" | "neutral" | "warning" } {
  if (isAdsChange(a)) {
    if (a.status === "executed") return { text: "Applied", tone: "success" };
    if (a.status === "rolled_back") return { text: "Undone", tone: "neutral" };
    if (a.status === "approved" && a.ads_status === "failed") return { text: "Not applied", tone: "danger" };
    if (a.status === "approved") return { text: "Approved, not applied", tone: "warning" };
  }
  return { text: sentence(a.status), tone: a.status === "approved" || a.status === "executed" ? "success" : a.status === "rejected" || a.status === "rolled_back" ? "danger" : "neutral" };
}
// Payload keys a reviewer never needs to read: the title is the heading, the rest is NOLA's own bookkeeping.
const HIDDEN = new Set(["title", "provider", "safety_check", "before", "spec", "finding_id", "seal", "targets_require_review"]);
const riskLabel = (risk: string) => sentence(String(risk || "unknown").toLowerCase());
const riskTone = (risk: string) => (risk === "HIGH" ? "danger" : risk === "MEDIUM" ? "warning" : "neutral");
const FIELDS: [keyof Approval, string][] = [
  ["description", "Description"],
  ["ai_reasoning", "Reasoning"],
  ["reasoning", "Reasoning"],
  ["expected_outcome", "Expected outcome"],
  ["risk_if_wrong", "Risk if wrong"],
  ["measurement_criteria", "How it is measured"],
  ["confidence_level", "Confidence"],
  ["submitted_by_name", "Submitted by"],
  ["reviewed_by_name", "Reviewed by"],
  ["notes", "Notes"],
];
/** KB-019U §9: a budget change on a campaign with a bid target needs the approver to check the target. */
const needsTargetCheck = (a: Approval) => proposed(a.payload).targets_require_review === true;
/** The payload as an object; older rows stored it as a JSON string. */
function proposed(payload: unknown): Record<string, unknown> {
  let value = payload;
  if (typeof value === "string") {
    try { value = JSON.parse(value); } catch { return value ? { value } : {}; }
  }
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
}
/** A proposed value as a reviewer reads it: "google_ads" is Google Ads, 1200 is 1,200. */
function ProposedValue({ value }: { value: unknown }) {
  if (typeof value === "string" && /^[a-z][a-z0-9]*(_[a-z0-9]+)*$/.test(value)) return <>{sentence(value)}</>;
  if (typeof value === "number") return <>{value.toLocaleString()}</>;
  return <Value value={value} />;
}
export default function Approvals() {
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState(
    params.has("record") ? "all" : "pending",
  );
  const data = useApi<Approval[]>(`/api/approvals?status=${status}`);
  const [notes, setNotes] = useState("");
  // KB-019U §9: a budget change on a campaign with a bid target is approved once the target is checked.
  const [targetChecked, setTargetChecked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  // Undo removes or puts back something in Google Ads: the button asks once more before it goes.
  const [confirmUndo, setConfirmUndo] = useState(false);
  const selected = data.data?.find(
    (a) => String(a.id) === params.get("record"),
  );
  // A link to a record that is no longer listed (decided, or never here) says so instead of showing nothing.
  const missingRecord = !!params.get("record") && !data.loading && !!data.data && !selected;
  // The target check belongs to one record: moving to another (back/forward, a link) clears it.
  useEffect(() => { setTargetChecked(false); }, [selected?.id]);
  function openReview(a: Approval) {
    setNotes("");
    setTargetChecked(false);
    setError("");
    setConfirmUndo(false);
    setParams({ record: String(a.id) });
  }
  async function send(path: string) {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      const r = await api<{ message?: string }>(path, { method: "POST", body: "{}" });
      setDone(r?.message || "Done.");
      setParams({});
      data.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : "That did not work");
    } finally {
      setBusy(false);
    }
  }
  async function decide(decision: string) {
    if (!selected) return;
    setBusy(true);
    setError("");
    try {
      const r = await api<{ message?: string; applied?: boolean }>(`/api/approvals/${selected.id}`, {
        method: "PUT",
        body: JSON.stringify({ decision, notes, ...(targetChecked ? { target_reviewed: true } : {}) }),
      });
      data.reload();
      // Approved but not applied in Google Ads: say so in red, with Apply in Google Ads right there.
      if (r?.applied === false) { setError(r.message || "Approved, but not applied."); return; }
      setDone(r?.message || "Decision recorded.");
      setParams({});
    } catch (e) {
      setError(e instanceof Error ? e.message : "Decision failed");
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageHeader
        title="Approvals"
        description="Approving a Google Ads change made in NOLA applies it in Google Ads straight away."
      />
      {done && (
        <p className="inline-notice success" role="status">
          <span>{done}</span>
          <button type="button" aria-label="Dismiss" onClick={() => setDone("")}>×</button>
        </p>
      )}
      {missingRecord && <p className="inline-notice" role="status"><span>That approval is not in this list any more. It may have been decided already.</span> <button type="button" className="text-button" onClick={() => setParams({})}>Show the list</button></p>}
      <section className="panel approvals-panel">
      <div className="toolbar">
        <FilterPill
          label="Status"
          value={status}
          onChange={setStatus}
          options={["pending", "approved", "rejected", "executed", "rolled_back", "all"].map((s) => ({ value: s, name: s === "all" ? "All" : sentence(s) }))}
        />
        <span className="toolbar-end">
          <span className="result-count" aria-live="polite">{data.loading ? "Loading…" : `${data.data?.length || 0} ${data.data?.length === 1 ? "approval" : "approvals"}`}</span>
          <button className="icon-button" aria-label="Refresh approvals" title="Refresh approvals" disabled={data.loading || data.refreshing} onClick={data.reload}><RefreshCw className={data.refreshing ? "spin" : ""} aria-hidden="true" /></button>
        </span>
      </div>
      {data.loading ? (
        <LoadingState label="Loading approvals" />
      ) : data.error ? (
        <ErrorState message={data.error} onRetry={data.reload} />
      ) : !data.data?.length ? (
        <EmptyState
          title="No approvals in this view"
          description="Google Ads changes from the Campaigns page and the weekly Ads review, and changes the AI analyst suggests, wait here for a decision."
          action={<Link className="button secondary" to="/ads">Open Portfolio, Ads</Link>}
        />
      ) : (
        <div>
          <div className="table-wrap">
            <table className="approvals-table">
              <thead>
                <tr>
                  <th>Action</th>
                  <th>Client</th>
                  <th>Risk</th>
                  <th>Status</th>
                  <th>Submitted</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((a) => (
                  // The whole row opens the review in place; the button keeps it reachable by keyboard.
                  <tr key={a.id} onClick={() => openReview(a)}>
                    <td className="approval-action">
                      <button
                        className="approval-open"
                        onClick={(e) => {
                          e.stopPropagation();
                          openReview(a);
                        }}
                      >
                        {actionLabel(a)}
                      </button>
                    </td>
                    <td className="approval-client">{a.client_name}</td>
                    {/* Colour only where something needs a look: high risk, a failed or unapplied decision. */}
                    <td>{riskTone(a.risk_level) === "danger" ? <StatusBadge tone="danger">{riskLabel(a.risk_level)} risk</StatusBadge> : <span className="muted">{riskLabel(a.risk_level)} risk</span>}</td>
                    <td>{statusOf(a).tone === "neutral" ? <span className="muted">{statusOf(a).text}</span> : <StatusBadge tone={statusOf(a).tone}>{statusOf(a).text}</StatusBadge>}</td>
                    <td className="approval-date">{formatDateTime(a.submitted_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      </section>
      {selected && (
        <Dialog
          title={actionLabel(selected)}
          description={selected.client_name ? String(selected.client_name) : undefined}
          onClose={() => setParams({})}
        >
          <div className="approval-review">
          <p className="approval-facts">
            <StatusBadge tone={riskTone(selected.risk_level)}>{`${riskLabel(selected.risk_level)} risk`}</StatusBadge>
            <StatusBadge tone={statusOf(selected).tone}>{statusOf(selected).text}</StatusBadge>
            <span className="muted">Submitted {formatDate(selected.submitted_at)}</span>
          </p>
          <dl className="record-fields">
            {FIELDS.filter(([k]) => selected[k] && !(k === "reasoning" && selected.ai_reasoning)).map(([k, name]) => (
              <div key={k}>
                <dt>{name}</dt>
                <dd>{k === "confidence_level" ? sentence(String(selected[k])) : String(selected[k])}</dd>
              </div>
            ))}
          </dl>
          <h3>Proposed change</h3>
          {Object.keys(proposed(selected.payload)).length ? (
            <dl className="record-fields">
              {Object.entries(proposed(selected.payload)).filter(([k]) => !(isAdsChange(selected) && HIDDEN.has(k))).map(([k, v]) => (
                <div key={k}>
                  <dt>{label(k)}</dt>
                  <dd>
                    {/* A grouped request (38 app placements) already lists every item in words;
                        its matching operations stay one click away instead of a second long table. */}
                    {k === "operations" && Array.isArray(v) && ("placements" in proposed(selected.payload) || isAdsChange(selected)) ? (
                      <details>
                        <summary>Exact changes ({v.length})</summary>
                        <ProposedValue value={v} />
                      </details>
                    ) : (
                      <ProposedValue value={v} />
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          ) : (
            <p className="approval-review-empty">No proposed values were recorded with this request.</p>
          )}
          <p className="panel-note">
            {isAdsChange(selected)
              ? "Check the account and the values first. Approving applies this in Google Ads straight away. Google checks it first, and if the campaign changed since this was proposed, nothing is sent."
              : "Check the account and the proposed values first. Approving records your decision."}
          </p>
          {isAdsChange(selected) && selected.ads_status === "failed" && selected.ads_error && (
            <p className="inline-notice danger" role="status">Google Ads did not apply it: {selected.ads_error}</p>
          )}
          {isAdsChange(selected) && selected.status === "approved" && (
            <div className="operation-actions">
              <button className="button" disabled={busy} onClick={() => void send(`/api/approvals/${selected.id}/apply`)}>{busy ? "Applying…" : "Apply in Google Ads"}</button>
            </div>
          )}
          {isAdsChange(selected) && selected.status === "executed" && selected.ads_undoable && user?.role === "superadmin" && (
            <div className="operation-actions">
              {confirmUndo ? (<>
                <span className="muted">{selected.action_type === "ads_campaign_create" ? "Removes the campaign from Google Ads." : "Puts it back in Google Ads."}</span>
                <button className="button secondary" onClick={() => setConfirmUndo(false)}>Keep it</button>
                <button className="button danger" disabled={busy} onClick={() => void send(`/api/approvals/${selected.id}/rollback`)}>{busy ? "Undoing…" : "Undo now"}</button>
              </>) : <button className="button secondary" disabled={busy} onClick={() => setConfirmUndo(true)}>Undo in Google Ads</button>}
            </div>
          )}
          {selected.status === "pending" && (
            <div className="operation-form">
              {needsTargetCheck(selected) && (
                <label className="campaigns-check">
                  <input type="checkbox" checked={targetChecked} onChange={(e) => setTargetChecked(e.target.checked)} />
                  <span>I checked the campaign's target still fits the new budget. {String(proposed(selected.payload).target_review || "")}</span>
                </label>
              )}
              <label>
                Decision notes
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={3}
                />
              </label>
              <div className="operation-actions">
                <button
                  className="button"
                  disabled={
                    busy ||
                    (needsTargetCheck(selected) && !targetChecked) ||
                    (selected.risk_level === "HIGH" &&
                      user?.role !== "superadmin")
                  }
                  onClick={() => decide("approved")}
                >
                  {busy ? "Working…" : isAdsChange(selected) ? "Approve and apply" : "Approve proposal"}
                </button>
                <button
                  className="button secondary"
                  disabled={busy}
                  onClick={() => decide("rejected")}
                >
                  Reject
                </button>
              </div>
              {selected.risk_level === "HIGH" &&
                user?.role !== "superadmin" && (
                  <p>A superadmin must approve this action.</p>
                )}
            </div>
          )}
          {error && <p role="alert" className="inline-notice danger">{error}</p>}
          </div>
        </Dialog>
      )}
    </>
  );
}
